import { useState, useRef, useCallback } from 'react';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useCreateShare, useRegenerateShare, useDisableShare } from '@/hooks/useShare';
import type { Wedding } from '@/lib/types';
import { track } from '@/lib/analytics';

interface ShareSectionProps {
  weddingId: string;
  wedding: Wedding | null | undefined;
}

export function ShareSection({ weddingId, wedding }: ShareSectionProps) {
  const [showPinSheet, setShowPinSheet] = useState(false);
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '']);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);

  const createShare = useCreateShare(weddingId);
  const regenerateShare = useRegenerateShare(weddingId);
  const disableShare = useDisableShare(weddingId);

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      const digit = value.replace(/\D/g, '').slice(-1);
      const next = [...pinDigits];
      next[index] = digit;
      setPinDigits(next);
      if (digit && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [pinDigits]
  );

  const handleGenerate = useCallback(
    (skipPin?: boolean) => {
      const pin = skipPin ? undefined : pinDigits.join('') || undefined;
      createShare.mutate(
        { sharePin: pin },
        {
          onSuccess: () => {
            track('share_link_created');
            setShowPinSheet(false);
            setPinDigits(['', '', '', '']);
          },
        }
      );
    },
    [pinDigits, createShare]
  );

  const handleCopy = useCallback(() => {
    if (!wedding?.shareId) return;
    const shareUrl = `${window.location.origin}/s/${wedding.shareId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [wedding?.shareId]);

  const handleNativeShare = useCallback(() => {
    if (!wedding?.shareId) return;
    const shareUrl = `${window.location.origin}/s/${wedding.shareId}`;
    navigator
      .share({ url: shareUrl, title: 'Wedding Timeline' })
      .catch(() => {
        // Not all browsers support navigator.share — fail silently
      });
  }, [wedding?.shareId]);

  if (!wedding?.sharingEnabled || !wedding?.shareId) {
    return (
      <>
        <div
          style={{
            background: 'var(--color-surface)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <button
            onClick={() => setShowPinSheet(true)}
            className="w-full"
            style={{
              height: 48,
              borderRadius: 10,
              border: 'none',
              background: 'var(--color-primary)',
              color: '#1A1A2E',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              transition: 'background 150ms ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'var(--color-primary-hover)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'var(--color-primary)';
            }}
          >
            Share Live Timeline
          </button>
          <p
            className="text-center mt-2"
            style={{ fontSize: 12, color: 'var(--color-text-muted)' }}
          >
            Allow vendors and guests to view your timeline online
          </p>
        </div>

        {/* PIN bottom sheet */}
        {showPinSheet && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 500,
              display: 'flex',
              alignItems: 'flex-end',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowPinSheet(false);
            }}
          >
            <div
              className="animate-slide-up"
              style={{
                background: 'var(--color-surface)',
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                padding: 24,
                width: '100%',
                maxWidth: 512,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  margin: 0,
                  textAlign: 'center',
                }}
              >
                Set a PIN (optional)
              </p>

              {/* 4-digit PIN inputs */}
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'center',
                  marginTop: 24,
                }}
              >
                {pinDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !pinDigits[i] && i > 0) {
                        inputRefs.current[i - 1]?.focus();
                      }
                    }}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'var(--color-background)',
                      color: 'var(--color-text)',
                      fontSize: 20,
                      textAlign: 'center',
                      fontFamily: 'var(--font-mono)',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border-focus)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                    }}
                  />
                ))}
              </div>

              {/* Generate Link */}
              <button
                onClick={() => handleGenerate(false)}
                disabled={createShare.isPending}
                className="w-full"
                style={{
                  marginTop: 24,
                  height: 48,
                  borderRadius: 10,
                  border: 'none',
                  background: 'var(--color-primary)',
                  color: '#1A1A2E',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: createShare.isPending ? 'not-allowed' : 'pointer',
                  opacity: createShare.isPending ? 0.7 : 1,
                  fontFamily: 'var(--font-body)',
                  transition: 'background 150ms ease',
                }}
              >
                {createShare.isPending ? 'Generating…' : 'Generate Link'}
              </button>

              {/* Skip PIN */}
              <button
                onClick={() => handleGenerate(true)}
                disabled={createShare.isPending}
                className="w-full"
                style={{
                  marginTop: 12,
                  height: 36,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  textAlign: 'center',
                }}
              >
                Skip PIN
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Enabled state
  const shareUrl = `${window.location.origin}/s/${wedding.shareId}`;

  return (
    <>
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: 16,
        }}
      >
        {/* Share URL */}
        <p
          style={{
            fontSize: 13,
            fontFamily: 'var(--font-mono)',
            color: 'rgba(255,255,255,0.7)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            userSelect: 'text',
            margin: 0,
          }}
        >
          {shareUrl}
        </p>

        {/* Action buttons */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
          }}
        >
          <button
            onClick={handleCopy}
            style={{
              height: 36,
              paddingLeft: 12,
              paddingRight: 12,
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface-hover)',
              color: copied ? '#4ADE80' : 'var(--color-text)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              transition: 'color 150ms ease',
              flexShrink: 0,
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>

          <button
            onClick={handleNativeShare}
            style={{
              height: 36,
              paddingLeft: 12,
              paddingRight: 12,
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface-hover)',
              color: 'var(--color-text)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              flexShrink: 0,
            }}
          >
            Share
          </button>

          <button
            onClick={() => setShowRegenerateDialog(true)}
            style={{
              height: 36,
              paddingLeft: 12,
              paddingRight: 12,
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface-hover)',
              color: 'var(--color-text-muted)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              flexShrink: 0,
            }}
          >
            Regenerate
          </button>
        </div>

        {/* Disable link */}
        <button
          onClick={() => setShowDisableDialog(true)}
          style={{
            marginTop: 16,
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: '#EF4444',
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          Disable Sharing
        </button>
      </div>

      <ConfirmDialog
        open={showRegenerateDialog}
        onOpenChange={setShowRegenerateDialog}
        title="Regenerate Link?"
        description="This will invalidate the current link. Anyone using it will lose access."
        confirmLabel="Regenerate"
        variant="destructive"
        onConfirm={() => regenerateShare.mutate({})}
      />

      <ConfirmDialog
        open={showDisableDialog}
        onOpenChange={setShowDisableDialog}
        title="Disable Sharing?"
        description="Anyone with the link will no longer be able to view the timeline."
        confirmLabel="Disable"
        variant="destructive"
        onConfirm={() => disableShare.mutate()}
      />
    </>
  );
}
